import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { LOCALES, translations, type Language } from "locales";

export type { Language };
export type TranslateParams = Record<string, string | number>;

interface LanguageContextType {
  language: Language;
  /** BCP-47 tag for Intl / toLocaleString calls. */
  locale: string;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: TranslateParams) => string;
}

const STORAGE_KEY = "language";

const interpolate = (template: string, params?: TranslateParams) =>
  params
    ? template.replace(/\{(\w+)\}/g, (match, name) =>
        name in params ? String(params[name]) : match
      )
    : template;

// Storage can throw in private mode or when site data is blocked.
const readStoredLanguage = (): Language => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "en" || stored === "vi" ? stored : "vi";
  } catch {
    return "vi";
  }
};

const LanguageContext = createContext<LanguageContextType>({
  language: "vi",
  locale: LOCALES.vi,
  setLanguage: () => {},
  t: (key, params) => interpolate(key, params),
});

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(readStoredLanguage);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // Preference just won't survive a reload.
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback(
    (key: string, params?: TranslateParams) =>
      interpolate(translations[language][key] ?? key, params),
    [language]
  );

  const value = useMemo(
    () => ({ language, locale: LOCALES[language], setLanguage, t }),
    [language, setLanguage, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
